import Point from "./Point";

export default class BoundaryRect {
  min: Point;
  max: Point;

  constructor(min?: Point, max?: Point) {
    this.min = min || new Point(0, 0);
    this.max = max || new Point(0, 0);
  }

  fromJSON(data: any) {
    this.min.fromJSON(data.min);
    this.max.fromJSON(data.max);
  }

  toJSON(): any {
    return {
      min: this.min,
      max: this.max,
    };
  }

  get width() {
    return this.max.x - this.min.x;
  }
  get height() {
    return this.max.y - this.min.y;
  }

  get center(): Point {
    return new Point((this.min.x + this.max.x) / 2, (this.min.y + this.max.y) / 2);
  }

  addPadding(padding: number): BoundaryRect {
    return new BoundaryRect(
      new Point(this.min.x - padding, this.min.y - padding),
      new Point(this.max.x + padding, this.max.y + padding),
    );
  }

  scale(factor: number): BoundaryRect {
    return new BoundaryRect(this.min.scale(factor), this.max.scale(factor));
  }

  get area(): number {
    return this.width * this.height;
  }

  copy(): BoundaryRect {
    return new BoundaryRect(new Point(this.min.x, this.min.y), new Point(this.max.x, this.max.y));
  }

  getOverlap(otherBoundary: BoundaryRect): null | BoundaryRect {
    const intersectionX1 = Math.max(this.min.x, otherBoundary.min.x);
    const intersectionX2 = Math.min(this.max.x, otherBoundary.max.x);

    if (intersectionX1 >= intersectionX2) {
      return null;
    }

    const intersectionY1 = Math.max(this.min.y, otherBoundary.min.y);
    const intersectionY2 = Math.min(this.max.y, otherBoundary.max.y);

    if (intersectionY1 >= intersectionY2) {
      return null;
    }

    return new BoundaryRect(new Point(intersectionX1, intersectionY1), new Point(intersectionX2, intersectionY2));
  }

  closestEdge(p: Point):
    | {
      side: "left" | "right" | "top" | "bottom"; // no itx
      distance: number;
      inside: boolean;
    }
    | undefined {
    //
    // distance to the rect center
    //
    const dx = p.x - this.center.x;
    const dy = p.y - this.center.y;

    let leftDistance = p.x - this.min.x;
    let rightDistance = p.x - this.max.x;

    let topDistance = p.y - this.min.y;
    let bottomDistance = p.y - this.max.y;

    let leftOutside = leftDistance < 0;
    let rightOutside = rightDistance > 0;
    let topOutside = topDistance < 0;
    let bottomOutside = bottomDistance > 0;

    if (leftOutside && topOutside) {
      return undefined;
    }

    if (leftOutside && bottomOutside) {
      return undefined;
    }

    if (rightOutside && topOutside) {
      return undefined;
    }

    if (rightOutside && bottomOutside) {
      return undefined;
    }

    //
    // distance to the nearest edge of the rect
    //

    let distance = 0;
    let inside = false;

    if (leftOutside) {
      distance = Math.abs(leftDistance);
    } else if (rightOutside) {
      distance = Math.abs(rightDistance);
    } else if (topOutside) {
      distance = Math.abs(topDistance);
    } else if (bottomOutside) {
      distance = Math.abs(bottomDistance);
    } else {
      distance = Math.min(
        Math.abs(leftDistance),
        Math.abs(rightDistance),
        Math.abs(topDistance),
        Math.abs(bottomDistance),
      );
      inside = true;
    }

    //
    // name of the closest edge
    //

    const ddx = Math.abs(Math.abs(dx) - this.width / 2);
    const ddy = Math.abs(Math.abs(dy) - this.height / 2);

    const side =
      Math.abs(ddx) < Math.abs(ddy)
        ? dx > 0
          ? "right"
          : "left" // no itx
        : dy > 0
          ? "bottom"
          : "top"; // no itx

    return { side: side, distance: distance, inside: inside };
  }
}
